// i18n configuration for ptc-statetax.
//
// Bilingual EN/ES is a Phase 1.6 scope fence — staff work in both languages
// and clients' Dataverse notes regularly come back in Spanish. This wires
// react-i18next with browser-language detection + localStorage persistence.
//
// Scope fence on what gets translated:
// - YES: UI chrome (nav labels, page titles, action buttons, empty/error messages)
// - NO:  Dataverse-sourced content (filing frequency labels, owner names,
//        information-source labels, client notes) — that's source-of-truth data,
//        not UI chrome. Dataverse FormattedValue annotations already render in
//        the tenant's configured language.
// - NO:  formatted numbers/dates — date-fns + Intl.NumberFormat handle locale.
//
// Key naming: hierarchical, dot-delimited, grouped by page or surface.
// Examples:
//   nav.dashboard, nav.tasks, nav.clients
//   common.back, common.loading, common.search
//   clientsPage.title, clientsPage.subtitle
//
// Add a new string: drop a key into both locales/en.json and locales/es.json,
// then `const { t } = useTranslation(); t('<your.new.key>')`.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import es from './locales/es.json';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'es'],
    interpolation: { escapeValue: false }, // React escapes already.
    detection: {
      // Priority: explicit user choice (localStorage) > browser preference > fallback.
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'ptc-statetax-lang',
    },
    returnEmptyString: false, // Empty string in a locale = fall back to fallbackLng.
  });

// <html lang> follows the UI language, so a screen reader pronounces Spanish as Spanish (fleet i18n
// standard §3). index.html can only hard-code the default.
const syncDocumentLang = (lng: string | undefined) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng?.startsWith('es') ? 'es' : 'en';
};
i18n.on('languageChanged', syncDocumentLang);
syncDocumentLang(i18n.resolvedLanguage ?? i18n.language);

export default i18n;
